import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC } from 'react';
import { CommonIcon } from '../common/CommonIcon';

interface VoiceModalAudioTranscriptProps {
  transcript: string | null;
  waveformBars: number[];
  playbackProgress: number;
  isPlaying: boolean;
  onPlayback: () => void;
  isProcessing?: boolean;
}

export const VoiceModalAudioTranscript: FC<VoiceModalAudioTranscriptProps> = ({
  transcript,
  waveformBars,
  playbackProgress,
  isPlaying,
  onPlayback,
  isProcessing = false,
}) => (
  <>
    {waveformBars.length > 0 && (
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: transcript ? 1.25 : 0 }}>
        <Box
          component="button"
          onClick={onPlayback}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '50%',
            border: 'none',
            background: 'var(--tg-theme-button-color)',
            color: 'var(--tg-theme-button-text-color)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CommonIcon
            icon={isPlaying ? 'pause' : 'play_arrow'}
            type="filled"
            size={22}
            color="var(--tg-theme-button-text-color)"
          />
        </Box>
        <Stack
          direction="row"
          alignItems="flex-end"
          gap={0.25}
          sx={{ flex: 1, height: 32, minHeight: 0 }}
        >
          {waveformBars.map((h, i) => {
            const n = waveformBars.length;
            const threshold = n > 0 ? (i + 0.5) / n : 0;
            const isPlayed = playbackProgress > threshold;
            return (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  minWidth: 2,
                  height: `${Math.max(4, h * 28)}px`,
                  borderRadius: 2,
                  background: isPlayed
                    ? 'linear-gradient(180deg, var(--tg-theme-button-color), color-mix(in srgb, var(--tg-theme-button-color) 70%, white))'
                    : 'color-mix(in srgb, white 20%, transparent)',
                  opacity: isPlayed ? 1 : 0.5,
                  transition: 'background 0.25s ease-out, opacity 0.25s ease-out',
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    )}
    {transcript && (
      <>
        <Text
          style={{
            fontSize: 10,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: 'var(--tg-theme-hint-color)',
            marginBottom: 6,
            display: 'block',
          }}
        >
          You said
        </Text>
        <Text
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: isProcessing ? 'var(--tg-theme-hint-color)' : 'var(--tg-theme-text-color)',
            fontStyle: isProcessing ? 'italic' : 'normal',
          }}
        >
          {transcript}
        </Text>
      </>
    )}
  </>
);
